/** Painting of the sign-in territory: pure functions over a canvas context and a scene state.
 *  Every colour is a design-system token read at runtime (light and dark mode). */

export interface Palette {
  map: string;
  line: string;
  line2: string;
  line3: string;
  sel: string;
  gold: string;
  ring: string;
  edge: string;
}

interface Walker {
  a: number;
  b: number;
  w: number;
  v: number;
  p: number;
  q: number;
}

export interface SceneState {
  cx: CanvasRenderingContext2D;
  palette: Palette;
  size: { w: number; h: number };
  node: { x: number; y: number; r: number };
  zoom: number;
  focused: boolean;
  walkers: readonly Walker[];
}

const WALKERS = 18;
const MARK_SIZE = 34;
const PULSE_SECONDS = 3.2;

export function readPalette(): Palette {
  const css = (name: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return {
    map: css('--map'),
    line: css('--map-line'),
    line2: css('--line-2'),
    line3: css('--line-3'),
    sel: css('--sel'),
    gold: css('--gold-fill'),
    ring: css('--mark-ring'),
    edge: css('--gold-edge'),
  };
}

export function createWalkers(): Walker[] {
  return Array.from({ length: WALKERS }, () => ({
    a: 0.25 + Math.random() * 0.7,
    b: 0.2 + Math.random() * 0.6,
    w: 0.03 + Math.random() * 0.05,
    v: 0.02 + Math.random() * 0.05,
    p: Math.random() * 6.3,
    q: Math.random() * 6.3,
  }));
}

type Point = readonly [number, number];

function line(cx: CanvasRenderingContext2D, [x1, y1]: Point, [x2, y2]: Point) {
  cx.beginPath();
  cx.moveTo(x1, y1);
  cx.lineTo(x2, y2);
  cx.stroke();
}

interface IsolineSet {
  ox: number;
  oy: number;
  rings: number;
  base: number;
  seed: number;
}

function isolines(s: SceneState, set: IsolineSet, time: number): void {
  const { cx, palette } = s;
  for (let k = 1; k <= set.rings; k += 1) {
    const r0 = k * set.base * s.zoom;
    cx.beginPath();
    for (let a = 0; a <= 64; a += 1) {
      const th = (a / 64) * Math.PI * 2;
      const wave =
        0.07 * Math.sin(3 * th + k * 0.7 + set.seed + time * 0.12) +
        0.04 * Math.sin(5 * th - k * 0.4 - time * 0.08);
      const x = set.ox + Math.cos(th) * r0 * (1 + wave);
      const y = set.oy + Math.sin(th) * r0 * (1 + wave) * 0.82;
      if (a === 0) cx.moveTo(x, y);
      else cx.lineTo(x, y);
    }
    cx.closePath();
    const major = k % 3 === 0;
    cx.globalAlpha = major ? 1 : 0.6;
    cx.lineWidth = major ? 1.4 : 1;
    cx.strokeStyle = major ? palette.line2 : palette.line;
    cx.stroke();
  }
  cx.globalAlpha = 1;
}

function sweep({ cx, palette, node, zoom, focused }: SceneState, time: number): void {
  const reach = node.r * 1.15 * zoom;
  const angle = (time * 0.45) % (Math.PI * 2);
  const gradient = cx.createRadialGradient(node.x, node.y, 0, node.x, node.y, reach);
  gradient.addColorStop(0, palette.sel);
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  cx.save();
  cx.globalAlpha = focused ? 0.16 : 0.1;
  cx.fillStyle = gradient;
  cx.beginPath();
  cx.moveTo(node.x, node.y);
  cx.arc(node.x, node.y, reach, angle - 0.5, angle);
  cx.closePath();
  cx.fill();
  cx.restore();
  cx.strokeStyle = palette.sel;
  cx.globalAlpha = focused ? 0.5 : 0.3;
  line(cx, [node.x, node.y], [node.x + Math.cos(angle) * reach, node.y + Math.sin(angle) * reach]);
  cx.globalAlpha = 1;
}

function operators({ cx, palette, node, zoom, focused, walkers }: SceneState, time: number): void {
  const spread = (focused ? 0.8 : 1) * zoom * node.r;
  cx.fillStyle = palette.line3;
  for (const w of walkers) {
    cx.beginPath();
    cx.arc(
      node.x + Math.cos(w.p + time * w.w) * w.a * spread,
      node.y + Math.sin(w.q + time * w.v) * w.b * spread,
      3,
      0,
      7,
    );
    cx.fill();
  }
}

/** The brand mark (ring, node, four ticks) with a slow pulse: a presence, never an alarm. */
function mark({ cx, palette, node }: SceneState, pulse: number | null): void {
  const { x, y } = node;
  const h = MARK_SIZE / 2;
  const tick = MARK_SIZE * 0.18;
  const rr = MARK_SIZE * 0.26;
  cx.lineWidth = Math.max(2, MARK_SIZE * 0.11);
  cx.strokeStyle = palette.ring;
  cx.beginPath();
  cx.roundRect(x - h, y - h, MARK_SIZE, MARK_SIZE, rr);
  cx.fillStyle = palette.map;
  cx.fill();
  cx.stroke();
  cx.lineCap = 'round';
  line(cx, [x, y - h - tick], [x, y - h]);
  line(cx, [x, y + h], [x, y + h + tick]);
  line(cx, [x - h - tick, y], [x - h, y]);
  line(cx, [x + h, y], [x + h + tick, y]);
  cx.fillStyle = palette.gold;
  cx.beginPath();
  cx.arc(x, y, MARK_SIZE * 0.19, 0, 7);
  cx.fill();
  if (pulse === null) return;
  cx.strokeStyle = palette.edge;
  cx.lineWidth = 1.5;
  cx.globalAlpha = 1 - pulse;
  cx.beginPath();
  cx.arc(x, y, MARK_SIZE * 0.7 + pulse * MARK_SIZE * 1.6, 0, 7);
  cx.stroke();
  cx.globalAlpha = 1;
}

/** One frame: the still background (ground and grid, `territory-background.ts`) copied as it is,
 *  then everything that moves. */
export function paintScene(
  s: SceneState,
  background: CanvasImageSource,
  time: number,
  still: boolean,
): void {
  const { cx, size, node } = s;
  cx.drawImage(background, 0, 0, size.w, size.h);
  isolines(s, { ox: node.x, oy: node.y, rings: 9, base: node.r * 0.13, seed: 0 }, time);
  const northEast = { ox: node.x + node.r * 1.05, oy: node.y - node.r * 0.85 };
  const northWest = { ox: node.x - node.r * 1.1, oy: node.y - node.r * 0.9 };
  isolines(s, { ...northEast, rings: 4, base: node.r * 0.1, seed: 2 }, time);
  isolines(s, { ...northWest, rings: 3, base: node.r * 0.09, seed: 4 }, time);
  sweep(s, time);
  operators(s, time);
  mark(s, still ? null : (time % PULSE_SECONDS) / PULSE_SECONDS);
}
